import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { LoginManager } from '../../core/login-manager';
import NotAuthorized from '../not_authorized';
import { isAdmin, isAdminGlobale } from '../../lib/ruoli';
import Utenti from './utenti';
import Utente from './utente';
import Sale from './sale';
import Sala from './sala';
import Anagrafiche from './anagrafiche';
import Palazzina from './palazzina';
import Regolamenti from './regolamenti';
import Regolamento from './regolamento';
import Impostazioni from './impostazioni';
import Condominio from './condominio';

const SECTIONS = [
    { path: '/admin', label: 'Utenti' },
    { path: '/admin/sale', label: 'Sale' },
    { path: '/admin/anagrafiche', label: 'Palazzine e appartamenti' },
    { path: '/admin/regolamenti', label: 'Regolamenti' },
    // per l'admin globale la stessa sezione e' l'elenco dei condomini,
    // con la creazione: chiamarla "Impostazioni" la renderebbe introvabile
    { path: '/admin/impostazioni', label: 'Impostazioni', labelGlobale: 'Condomini' },
];

/** Vecchio indirizzo della scheda nell'area admin: rimanda a quello nuovo. */
function AllaPrenotazione() {
    const { id } = useParams();
    return <Navigate to={`/prenotazioni/${id}`} replace />;
}

/**
 * Area di amministrazione interna al frontend (nessuna sidebar).
 * Accesso riservato agli amministratori: livello 0 (globale) e 1 (condominio).
 *
 * Questo componente fa da guscio: barra delle sezioni e routing interno.
 * Ogni sezione vive nel proprio file e parla con l'API tramite DataManager.
 */
export default function Admin() {
    const [userInfo, setUserInfo] = useState(null);
    const [ready, setReady] = useState(false);
    const location = useLocation();
    const tabs = useRef(null);

    useEffect(() => {
        setUserInfo(LoginManager.getUserInfo());
        setReady(true);
    }, []);

    /*
     * Su schermo stretto la barra delle sezioni scorre in orizzontale: senza
     * questo, arrivando su una sezione in fondo la tab accesa resterebbe
     * fuori campo e non si capirebbe dove ci si trova.
     */
    useEffect(() => {
        const nav = tabs.current;
        if (!nav) return;
        const attiva = nav.querySelector('.lv-tab.active');
        // jsdom non implementa scrollIntoView: meglio non darlo per scontato
        if (attiva && attiva.scrollIntoView) {
            attiva.scrollIntoView({ inline: 'nearest', block: 'nearest' });
        }
    }, [location.pathname, ready]);

    if (!ready) return null;
    if (!isAdmin(userInfo)) return <NotAuthorized />;

    const globale = isAdminGlobale(userInfo);

    return (
        <main className="lv-shell lv-stack">
            <nav className="lv-tabs lv-tabs-standalone" ref={tabs}>
                {SECTIONS.map(s => {
                    // la tab resta accesa anche sulla pagina di dettaglio
                    const active = s.path === '/admin'
                        ? (location.pathname === '/admin' || location.pathname.startsWith('/admin/utenti'))
                        : location.pathname.startsWith(s.path);
                    return (
                        <Link key={s.path} to={s.path} className={`lv-tab${active ? ' active' : ''}`}>
                            {globale && s.labelGlobale ? s.labelGlobale : s.label}
                        </Link>
                    );
                })}
            </nav>

            <Routes>
                <Route index element={<Utenti />} />
                <Route path="utenti" element={<Utenti />} />
                <Route path="utenti/nuovo" element={<Utente />} />
                <Route path="utenti/:id" element={<Utente />} />
                {/* il registro e' confluito in /prenotazioni: i vecchi link ci portano */}
                <Route path="prenotazioni" element={<Navigate to="/prenotazioni" replace />} />
                <Route path="prenotazioni/:id" element={<AllaPrenotazione />} />
                <Route path="sale" element={<Sale />} />
                <Route path="sale/nuova" element={<Sala />} />
                <Route path="sale/:id" element={<Sala />} />
                <Route path="anagrafiche" element={<Anagrafiche />} />
                <Route path="anagrafiche/nuova" element={<Palazzina />} />
                <Route path="anagrafiche/:id" element={<Palazzina />} />
                <Route path="regolamenti" element={<Regolamenti />} />
                <Route path="regolamenti/nuovo" element={<Regolamento />} />
                <Route path="regolamenti/:id" element={<Regolamento />} />
                <Route path="impostazioni" element={<Impostazioni userInfo={userInfo} />} />
                <Route path="impostazioni/nuovo" element={<Condominio />} />
                <Route path="impostazioni/:id" element={<Condominio />} />
                <Route path="*" element={<Utenti />} />
            </Routes>
        </main>
    );
}
